import { BreathDuration } from '@breath-lab/types';
import { BreathworkConfiguration } from '@breath-lab/types';
import { BreathworkConfigurationUi, isBreathworkRuleValid } from '@breath-lab/types';
import type { UserSettings } from '@breath-lab/types';

function inRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

/**
 * Validates a BreathworkConfiguration against its UI constraints.
 * Returns an array of human-readable error messages. Empty = valid.
 * Port of the validation logic in BreathworkConfigurationScreen._onPlayTapped().
 */
export function validateConfiguration(
  config: BreathworkConfiguration,
  configUi: BreathworkConfigurationUi,
): string[] {
  const errors: string[] = [];

  const { min: rMin, max: rMax } = configUi.numberOfRounds;
  if (!inRange(config.rounds, rMin, rMax)) {
    errors.push(`Number of rounds must be between ${rMin} and ${rMax}`);
  }

  if (configUi.numberOfBreathsOnFirstRound != null) {
    const { min, max } = configUi.numberOfBreathsOnFirstRound;
    if (!inRange(config.breathsOnFirstRound ?? 0, min, max)) {
      errors.push(`Number of breaths in the first round must be between ${min} and ${max}`);
    }
  }

  const inhaleSec = config.breathDuration.inhale / 1000;
  const fullLungsSec = config.breathDuration.fullLungsRetention / 1000;
  const exhaleSec = config.breathDuration.exhale / 1000;
  const emptyLungsSec = config.breathDuration.emptyLungsRetention / 1000;

  if (configUi.inhaleDurationInSec != null) {
    const { min, max } = configUi.inhaleDurationInSec;
    if (!inRange(inhaleSec, min, max)) {
      errors.push(`Inhale duration must be between ${min} and ${max} seconds`);
    }
  }

  if (configUi.fullLungsRetentionDurationInSec != null) {
    const { min, max } = configUi.fullLungsRetentionDurationInSec;
    if (!inRange(fullLungsSec, min, max)) {
      errors.push(`Full lungs retention duration must be between ${min} and ${max} seconds`);
    }
  }

  if (configUi.exhaleDurationInSec != null) {
    const { min, max } = configUi.exhaleDurationInSec;
    if (!inRange(exhaleSec, min, max)) {
      errors.push(`Exhale duration must be between ${min} and ${max} seconds`);
    }
  }

  if (configUi.emptyLungsRetentionDurationInSec != null) {
    const { min, max } = configUi.emptyLungsRetentionDurationInSec;
    if (!inRange(emptyLungsSec, min, max)) {
      errors.push(`Empty lungs retention duration must be between ${min} and ${max} seconds`);
    }
  }

  // allBreathingStagesDurationsInSec: all 4 stages share the same value (inhale is representative)
  if (configUi.allBreathingStagesDurationsInSec != null) {
    const { min, max } = configUi.allBreathingStagesDurationsInSec;
    if (!inRange(inhaleSec, min, max)) {
      errors.push(`Breathing duration must be between ${min} and ${max} seconds`);
    }
  }

  // inhaleExhaleStagesDurationsInSec: inhale and exhale share the same value
  if (configUi.inhaleExhaleStagesDurationsInSec != null) {
    const { min, max } = configUi.inhaleExhaleStagesDurationsInSec;
    if (!inRange(inhaleSec, min, max)) {
      errors.push(`Breathing duration must be between ${min} and ${max} seconds`);
    }
  }

  for (const rule of configUi.rules ?? []) {
    if (!isBreathworkRuleValid(rule, config)) {
      errors.push(rule.message);
    }
  }

  return errors;
}

function buildDefaultBreathDuration(
  breathworkId: string,
  configUi: BreathworkConfigurationUi,
): BreathDuration {
  // These techniques have fixed breath durations regardless of configUi
  if (breathworkId === 'BHASTRIKA') {
    return { inhale: 250, fullLungsRetention: 0, exhale: 250, emptyLungsRetention: 0 };
  }
  if (breathworkId === 'KAPALABHATI') {
    return { inhale: 300, fullLungsRetention: 0, exhale: 250, emptyLungsRetention: 0 };
  }

  let inhale = 0;
  let fullLungsRetention = 0;
  let exhale = 0;
  let emptyLungsRetention = 0;

  if (configUi.allBreathingStagesDurationsInSec != null) {
    const ms = configUi.allBreathingStagesDurationsInSec.defaultValue * 1000;
    inhale = ms; fullLungsRetention = ms; exhale = ms; emptyLungsRetention = ms;
  } else if (configUi.inhaleExhaleStagesDurationsInSec != null) {
    const ms = configUi.inhaleExhaleStagesDurationsInSec.defaultValue * 1000;
    inhale = ms; exhale = ms;
  } else {
    inhale = (configUi.inhaleDurationInSec?.defaultValue ?? 0) * 1000;
    fullLungsRetention = (configUi.fullLungsRetentionDurationInSec?.defaultValue ?? 0) * 1000;
    exhale = (configUi.exhaleDurationInSec?.defaultValue ?? 0) * 1000;
    emptyLungsRetention = (configUi.emptyLungsRetentionDurationInSec?.defaultValue ?? 0) * 1000;
  }

  return { inhale, fullLungsRetention, exhale, emptyLungsRetention };
}

/**
 * Builds a BreathworkConfiguration populated with default values from BreathworkConfigurationUi.
 * Audio settings default to on (matching Flutter defaults). Retention toggles default to off.
 * Port of BreathworkConfigurationScreen.initState() + _onPlayTapped() construction logic.
 */
export function buildDefaultConfiguration(
  breathworkId: string,
  configUi: BreathworkConfigurationUi,
): BreathworkConfiguration {
  return {
    id: breathworkId,
    rounds: configUi.numberOfRounds.defaultValue,
    breathsOnFirstRound: configUi.numberOfBreathsOnFirstRound?.defaultValue ?? null,
    incrementBetweenRounds: configUi.allowIncrementBetweenRounds == null ? false : true,
    // Retention toggles default to off (null = disabled, not just unchecked)
    fullLungsRetentionOnRoundEnd: null,
    fullLungsRetentionOnBreathworkEnd: null,
    breathDuration: buildDefaultBreathDuration(breathworkId, configUi),
    isMusicOn: true,
    isGuidingVoiceOn: true,
    isCountdownOn: true,
    isBreathingSoundOn: true,
    isSkipTutorialOn: false,
  };
}

/**
 * Resolves a player-ready BreathworkConfiguration by merging saved config
 * (or defaults) with the user's persistent audio/tutorial preferences.
 */
export function resolvePlayerConfig(
  breathworkId: string,
  configUi: BreathworkConfigurationUi,
  userSettings: UserSettings | null,
  savedConfig: BreathworkConfiguration | null,
): BreathworkConfiguration {
  const base = savedConfig ?? buildDefaultConfiguration(breathworkId, configUi);
  return {
    ...base,
    isMusicOn: userSettings?.audio.isMusicOn ?? base.isMusicOn,
    isGuidingVoiceOn: userSettings?.audio.isGuidingVoiceOn ?? base.isGuidingVoiceOn,
    isCountdownOn: userSettings?.audio.isCountdownOn ?? base.isCountdownOn,
    isBreathingSoundOn: userSettings?.audio.isBreathingSoundOn ?? base.isBreathingSoundOn,
    isSkipTutorialOn: userSettings?.isSkipTutorialOn ?? base.isSkipTutorialOn,
  };
}
